import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone, Instagram, ChevronDown } from "lucide-react";
import { SITE, TikTokIcon } from "../lib/site";
import { MaskedLine } from "./Reveal";

const ParticleField = () => {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        const ctx = canvas.getContext("2d");
        let raf;
        let w, h;
        const dots = Array.from({ length: 70 }, () => ({
            x: Math.random(),
            y: Math.random(),
            r: Math.random() * 1.6 + 0.4,
            vx: (Math.random() - 0.5) * 0.0004,
            vy: (Math.random() - 0.5) * 0.0003,
            p: Math.random() * Math.PI * 2,
        }));

        const resize = () => {
            w = canvas.width = canvas.offsetWidth;
            h = canvas.height = canvas.offsetHeight;
        };
        resize();
        window.addEventListener("resize", resize);

        const draw = (t) => {
            ctx.clearRect(0, 0, w, h);
            const gx = w * (0.7 + 0.1 * Math.sin(t * 0.0002));
            const gy = h * (0.3 + 0.08 * Math.cos(t * 0.00025));
            const glow = ctx.createRadialGradient(gx, gy, 0, gx, gy, w * 0.45);
            glow.addColorStop(0, "rgba(255,255,255,0.07)");
            glow.addColorStop(1, "rgba(255,255,255,0)");
            ctx.fillStyle = glow;
            ctx.fillRect(0, 0, w, h);
            for (const d of dots) {
                d.x = (d.x + d.vx + 1) % 1;
                d.y = (d.y + d.vy + 1) % 1;
                const a = 0.15 + 0.25 * (0.5 + 0.5 * Math.sin(t * 0.001 + d.p));
                ctx.beginPath();
                ctx.arc(d.x * w, d.y * h, d.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255,255,255,${a})`;
                ctx.fill();
            }
            raf = requestAnimationFrame(draw);
        };
        raf = requestAnimationFrame(draw);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
        };
    }, []);

    return <canvas ref={ref} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
};

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    return (
        <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
            <motion.div style={{ y: imgY }} className="absolute inset-0 -z-10">
                <img
                    src="/assets/work-2.webp"
                    alt="Freshly detailed black sedan at a Miami home"
                    className="w-full h-[120%] object-cover object-center opacity-60"
                />
                <div className="absolute inset-0 bg-black/45" />
                <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#050505] to-transparent" />
            </motion.div>
            <ParticleField />

            <motion.div style={{ opacity: fade }} className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-40">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="inline-flex items-center gap-2.5 border border-white/15 bg-white/5 backdrop-blur-md rounded-full px-4 py-2 mb-8"
                    data-testid="hero-badge"
                >
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                    </span>
                    <span className="font-code text-[11px] uppercase tracking-[0.25em] text-zinc-300">
                        Mobile Detailing · We Come To You
                    </span>
                </motion.div>

                <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
                    <MaskedLine delay={0.45}>Flawless detailing,</MaskedLine>
                    <MaskedLine delay={0.6}>
                        delivered to <span className="text-zinc-500">your</span>
                    </MaskedLine>
                    <MaskedLine delay={0.75}>
                        <span className="italic text-zinc-400">doorstep.</span>
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.05 }}
                    className="mt-7 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed"
                >
                    Pronto Auto Care is Miami's mobile detailing unit. Showroom-level interiors,
                    mirror-gloss paint — at your home, office, or anywhere in South Florida.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.2 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <motion.a
                        data-testid="hero-book-now-button"
                        href={SITE.booksy}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        className="inline-flex items-center gap-2 bg-white text-black font-semibold px-8 py-4 rounded-full text-sm sm:text-base"
                    >
                        Book Your Detail <ArrowUpRight className="w-5 h-5" />
                    </motion.a>
                    <motion.a
                        data-testid="hero-call-now-button"
                        href={SITE.phoneHref}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        className="inline-flex items-center gap-2 border border-white/25 hover:border-white/60 transition-colors duration-300 text-white font-semibold px-8 py-4 rounded-full text-sm sm:text-base"
                    >
                        <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
                    </motion.a>
                    <div className="flex items-center gap-3 ml-1">
                        <a
                            data-testid="hero-instagram-link"
                            href={SITE.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram"
                            className="p-3 rounded-full border border-white/15 text-zinc-400 hover:text-white hover:border-white/50 transition-colors duration-300"
                        >
                            <Instagram className="w-4 h-4" />
                        </a>
                        <a
                            data-testid="hero-tiktok-link"
                            href={SITE.tiktok}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="TikTok"
                            className="p-3 rounded-full border border-white/15 text-zinc-400 hover:text-white hover:border-white/50 transition-colors duration-300"
                        >
                            <TikTokIcon className="w-4 h-4" />
                        </a>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.5 }}
                    className="mt-16 flex items-center justify-between font-code text-[10px] sm:text-xs uppercase tracking-[0.25em] text-zinc-500"
                >
                    <span data-testid="hero-coordinates">25.7617° N — 80.1918° W</span>
                    <span className="hidden sm:inline">Miami, Florida</span>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 text-zinc-500"
            >
                <ChevronDown className="w-5 h-5 animate-bounce" />
            </motion.div>
        </section>
    );
};
